import { useEffect, useState, type FormEvent } from "react";
import { api, type Cap, type Me } from "../lib/api";
import { checkPin, clearPin, pinSupported, setPin } from "../lib/pin";

/**
 * Two gates in one component, because they are the same gate at different
 * strengths: an email and a password, or a six digit PIN against a session that
 * already exists.
 *
 * The PIN never reaches the server (ADR 0004), so this screen works entirely
 * offline once the session cookie is in the browser. It is a lock on a phone in
 * a lorry, not an authentication factor.
 */
export function Login({
  mode = "password",
  title,
  subtitle,
  onDone,
  onCancel,
}: {
  mode?: "password" | "pin";
  title?: string;
  subtitle?: string;
  onDone: (m: Me | null) => void;
  onCancel?: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [offerPin, setOfferPin] = useState(false);
  const [newPin, setNewPin] = useState("");

  useEffect(() => {
    if (mode === "pin" && !pinSupported()) {
      setError("PIN tidak tersedia di koneksi tidak aman. Gunakan kata sandi.");
    }
  }, [mode]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "pin") {
        if (await checkPin(pin)) onDone(null);
        else {
          setError("PIN salah.");
          setPin("");
        }
        return;
      }
      const r = await api.login(email.trim(), password);
      onDone({
        user: r.user,
        roles: r.roles,
        capabilities: r.capabilities as Cap[],
      } as Me);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gagal masuk.");
    } finally {
      setBusy(false);
    }
  }

  async function savePin() {
    setError(null);
    try {
      await setPin(newPin);
      setOfferPin(false);
      setNewPin("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gagal menyimpan PIN.");
    }
  }

  return (
    <div className="auth-wrap">
      <form className="auth-card" onSubmit={submit}>
        <div className="rowflex">
          <div className="brand-mark" aria-hidden>
            GP
          </div>
          <div>
            <div className="brand-name">GEOpermitLIVE</div>
            <div className="brand-sub">Izin lokasi & deployment</div>
          </div>
        </div>

        <h1>{mode === "pin" ? (title ?? "Masuk") : "Masuk"}</h1>
        <p className="dim small" style={{ marginTop: 0 }}>
          {mode === "pin"
            ? (subtitle ?? "Masukkan PIN 6 angka")
            : "Gunakan email dan kata sandi dari admin."}
        </p>

        {mode === "pin" ? (
          <>
            <div className="pin-dots" aria-hidden>
              {Array.from({ length: 6 }, (_, i) => (
                <span key={i} className={`pin-dot ${i < pin.length ? "on" : ""}`} />
              ))}
            </div>
            <input
              className="input mono center"
              style={{ letterSpacing: 8, textAlign: "center", fontSize: 18 }}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              aria-label="PIN"
            />
            <div className="pinpad">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"].map((k, i) =>
                k === "" ? (
                  <span key={i} />
                ) : (
                  <button
                    key={i}
                    type="button"
                    className="btn"
                    onClick={() =>
                      setPin((p) => (k === "⌫" ? p.slice(0, -1) : (p + k).slice(0, 6)))
                    }
                  >
                    {k}
                  </button>
                ),
              )}
            </div>
          </>
        ) : (
          <>
            <div className="stack" style={{ marginTop: 14 }}>
              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  className="input"
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="pw">Kata sandi</label>
                <input
                  id="pw"
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            {error && (
              <p className="warn warn-red" style={{ marginTop: 12 }}>
                {error}
              </p>
            )}
            <button className="btn btn-primary" style={{ width: "100%", marginTop: 16 }} disabled={busy}>
              {busy ? "Memeriksa…" : "Masuk"}
            </button>

            {offerPin ? (
              <div className="stack mt16">
                <div className="field">
                  <label htmlFor="np">PIN baru (6 angka)</label>
                  <input
                    id="np"
                    className="input mono"
                    inputMode="numeric"
                    maxLength={6}
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
                <div className="rowflex">
                  <button type="button" className="btn btn-sm" onClick={savePin}>
                    Simpan PIN
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => {
                      clearPin();
                      setOfferPin(false);
                    }}
                  >
                    Hapus PIN
                  </button>
                </div>
              </div>
            ) : (
              pinSupported() && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm mt8"
                  style={{ width: "100%" }}
                  onClick={() => setOfferPin(true)}
                  title="Minta PIN 6 angka setiap kali aplikasi dibuka di perangkat ini"
                >
                  Atur PIN untuk perangkat ini
                </button>
              )
            )}
          </>
        )}

        {mode === "pin" && error && (
          <p className="warn warn-red" style={{ marginTop: 12 }}>
            {error}
          </p>
        )}

        {onCancel && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ width: "100%", marginTop: 8 }}
            onClick={onCancel}
          >
            Keluar sepenuhnya
          </button>
        )}
      </form>
    </div>
  );
}
